/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import { MarkerType } from '@xyflow/react';
import Framework from '@greenville/framework';
import { v4 as uuidv4 } from 'uuid';
// import { EDGE_COLORS } from '../constants/StyleConstants';
import {
  AUTH_METHOD,
  AUTH_TYPE,
  BERLIN_TENANT_ID_HEADER,
  CONTENT_TOOL_CONFIG,
  DEFAULT_EDGE_LABEL,
  EDGE_TYPE,
  FLOW_CONFIG,
  fromBackendAgentType,
  HTTP_METHOD,
  isAgentToAgentType,
  LEGACY_RUBRIC_LIST_DISPLAY_NAME,
  NODE_TYPE,
  SHARED_ACCESS_TYPE
} from '../constants/StudioTextConstants';
import { colors } from '../constants/theme';
import env from '../../../common/env';
import { scheduleFromRubricData } from '../components/studio/graphEvaluation/graphEvaluationUtils';
import {
  getSystemPromptFromDocument,
  getUserPromptFromDocument,
  mapResolvedDocument,
  decodePromptContent,
  resolvedPromptMatchesRefs
} from './promptApiMappers';
import { normalizeMiddlewareForStorage } from './middlewareUtils';
import { isCataloguePromptEditorMode } from './promptEditorModeUtils';

/** Default graph node `jsMethodCode` / `config.implementation.code` — delegates to `callApi`. */
export const DEFAULT_GRAPH_JS_METHOD_CODE = `async (params) => {
  const data = await callApi(apiConfig, apiParams);
  return data;
}`;

const DEFAULT_AGENT_MEMORY = {
  type: 'buffer',
  maxMessages: 10,
  reflectionThreshold: 5
};

/** Editor/API memory shape with defaults (canvas `config.memory` and backend `agentConfig.memory`). */
export function normalizeAgentMemory(memory) {
  if (!memory || typeof memory !== 'object') {
    return { ...DEFAULT_AGENT_MEMORY };
  }
  const maxMessages = Number(memory.maxMessages);
  return {
    type: memory.type || 'buffer',
    maxMessages: Number.isFinite(maxMessages) ? maxMessages : DEFAULT_AGENT_MEMORY.maxMessages,
    reflectionThreshold: memory.reflectionThreshold ?? DEFAULT_AGENT_MEMORY.reflectionThreshold
  };
}

/**
 * Studio graph editor URL
 * `/berlin/:tenantName/studio/graph/:graphId/:version`
 *
 * @param {string|null|undefined} tenantName
 * @param {string|null|undefined} graphId
 * @param {string|number|null|undefined} graphVersion
 * @returns {string|null}
 */
export function buildStudioGraphEditorUrl(tenantName, graphId, graphVersion) {
  if (typeof window === 'undefined' || !tenantName || !graphId || graphVersion == null || graphVersion === '')
    return null;
  const { origin } = window.location;
  return `${origin}/berlin/${tenantName}/studio/graph/${graphId}/${graphVersion}`;
}

/**
 * Graph nodes only: parameters from toolConfig.zodSchema as plain JSON (no base64 decode).
 */
const parseGraphZodSchemaPlain = (zodSchema) => {
  const fallback = { type: 'object', properties: {}, required: [] };
  if (zodSchema == null || zodSchema === '') {
    return fallback;
  }
  if (typeof zodSchema === 'object' && !Array.isArray(zodSchema)) {
    return zodSchema;
  }
  try {
    return JSON.parse(String(zodSchema).trim());
  } catch {
    return fallback;
  }
};

/**
 * Parse tool/skill zodSchema from API: may be JSON string or base64 (utils.encodedData).
 * Graph nodes use parseGraphZodSchemaPlain (plain JSON only).
 */
export const parseZodSchemaFromToolConfig = (zodSchema) => {
  const fallback = { type: 'object', properties: {}, required: [] };
  if (zodSchema == null || zodSchema === '') {
    return fallback;
  }
  if (typeof zodSchema === 'object' && !Array.isArray(zodSchema)) {
    return zodSchema;
  }
  const s = String(zodSchema).trim();
  try {
    return JSON.parse(s);
  } catch {
    try {
      const binary = atob(s.replace(/\s/g, ''));
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
      }
      const decoded = new TextDecoder().decode(bytes);
      return JSON.parse(decoded);
    } catch {
      return fallback;
    }
  }
};

/**
 * Show notification via Framework store
 * @param {string} type - Notification type ('error', 'info', 'success', 'warning')
 * @param {string} message - Notification message
 */
export const showNotification = (type, message) => {
  Framework.getStoreRegistry().getStore('notificationState').set({
    open: true,
    type,
    message
  });
};

/**
 * Get edge color based on source and target node types
 * @param {string} sourceType - Source node type
 * @param {string} targetType - Target node type
 * @returns {string} Edge color
 */
export const getEdgeColor = (sourceType, targetType) => {
  if (sourceType === NODE_TYPE.AGENT && targetType === NODE_TYPE.TOOL) {
    return colors.nodeTypes.agent;
  }
  if (sourceType === NODE_TYPE.TOOL) {
    return colors.nodeTypes.tool;
  }
  if (sourceType === NODE_TYPE.SKILL || sourceType === NODE_TYPE.GRAPH) {
    return sourceType === NODE_TYPE.GRAPH ? colors.nodeTypes.graph : colors.nodeTypes.skill;
  }
  if (sourceType === NODE_TYPE.START) {
    return colors.nodeTypes.start;
  }
  if (sourceType === NODE_TYPE.END) {
    return colors.nodeTypes.end;
  }
  if (sourceType === NODE_TYPE.SUPERVISOR) {
    return colors.nodeTypes.supervisor;
  }
  return colors.nodeTypes.agent;
};

const resolveLoadedPromptText = (primary, fallbackEncoded) => {
  if (primary != null) {
    return primary;
  }
  return decodePromptContent(fallbackEncoded) ?? '';
};

const buildLoadedAgentConfig = (
  agentConfig,
  mergedOtherParameters,
  parsers,
  isA2A,
  promptOptions = {},
  isSupervisor = false
) => {
  const { promptRef, promptVersionRef, resolvedPrompt, systemPrompt, userPrompt } = promptOptions;
  const hasPromptRefs = Boolean(promptRef && promptVersionRef);

  const sharedConfig = {
    ...(hasPromptRefs && { promptRef, promptVersionRef }),
    ...(resolvedPrompt && { resolvedPrompt }),
    ...(agentConfig?.bindingVersionMismatch && { bindingVersionMismatch: true }),
    ...(agentConfig?.promptCatalogueName && { promptCatalogueName: agentConfig.promptCatalogueName }),
    otherParameters: mergedOtherParameters,
    providerParameters: agentConfig?.providerParameters || {},
    additionalMetadata: agentConfig?.additionalMetadata,
    systemPrompt: resolveLoadedPromptText(systemPrompt, agentConfig?.prompt),
    userPrompt: resolveLoadedPromptText(userPrompt, agentConfig?.userPrompt),
    isInternal: agentConfig?.isInternal ?? false,
    memory: normalizeAgentMemory(agentConfig?.memory),
    toolCalling: {
      enabled: true,
      toolChoice: 'auto'
    },
    parsers: parsers || []
  };

  const normalizedMiddleware = normalizeMiddlewareForStorage(agentConfig?.middleware);
  if (normalizedMiddleware && !isSupervisor) {
    sharedConfig.middleware = normalizedMiddleware;
  }

  if (isA2A) {
    return {
      ...sharedConfig,
      model: {
        provider: agentConfig?.provider || '',
        model: agentConfig?.model || '',
        maxTokens: agentConfig?.maxTokens || 1000
      }
    };
  }

  return {
    ...sharedConfig,
    model: {
      provider: agentConfig?.provider || 'open-ai',
      model: agentConfig?.model || 'gpt-5-mini',
      maxTokens: agentConfig?.maxTokens || 1000,
      promptType: agentConfig?.promptType || ''
    },
    template: '{{input}}',
    functions: [],
    state: {
      variables: []
    }
  };
};

/** UUID v4 with dashes replaced by underscores — used in node/tool id prefixes. */
export const uuidWithUnderscores = () => uuidv4().replace(/-/g, '_');

/** Fixed node ids for graph entry/exit (node `type` remains start/end). */
export const NODE_ID_START = 'node_start';
export const NODE_ID_END = 'node_end';

export const countDynamicNonToolNodes = (nodes) =>
  nodes.filter((n) => n.type !== NODE_TYPE.TOOL && n.type !== NODE_TYPE.START && n.type !== NODE_TYPE.END).length;

/** Non-tool dynamic nodes: agent, skill, graph, supervisor, default, etc. */
export const generateDynamicNodeId = () => `node_${uuidWithUnderscores()}`;

export const generateToolNodeId = () => `tool_${uuidWithUnderscores()}`;

export const isContentToolApiEndpoint = (endpoint) =>
  String(endpoint || '').includes(CONTENT_TOOL_CONFIG.API_SEARCH_PATH);

export const isContentToolNodeData = (nodeData) =>
  !!(
    nodeData?.toolConfig?.apiConfig &&
    !nodeData?.toolConfig?.mcpConfig &&
    isContentToolApiEndpoint(nodeData?.toolConfig?.apiConfig?.endpoint)
  );

/** Canvas node id is source of truth; content-upload tools keep fileId as toolId. */
export const resolveToolDataId = (storedToolId, nodeData = null) => {
  if (nodeData && isContentToolNodeData(nodeData) && storedToolId) {
    return storedToolId;
  }
  return storedToolId || generateToolNodeId();
};

export const resolveNodeDataId = (canvasNodeId, storedId) => canvasNodeId || storedId || generateDynamicNodeId();

/**
 * Transform a loaded node from backend format to React Flow format
 * @param {Object} node - Node data from backend
 * @param {number} index - Node index for default positioning
 * @returns {Object} Transformed node for React Flow
 */
export const transformLoadedNode = (node, index) => {
  const normalizedCustomType = String(node?.customType || '').toLowerCase();
  const nodeGrouping = {};
  if (node?.parentId) nodeGrouping.parentId = node?.parentId;
  if (node?.extent) nodeGrouping.extent = node?.extent;
  if (node?.positionAbsolute) nodeGrouping.positionAbsolute = node?.positionAbsolute;
  if (node?.width) nodeGrouping.width = node?.width;
  if (node?.height) nodeGrouping.height = node?.height;

  // Base node structure
  const baseNode = {
    id: node.id,
    type: normalizedCustomType || NODE_TYPE.DEFAULT,
    position: node.position || {
      x: 100 + index * 150,
      y: 100 + index * 50
    },
    draggable: true,
    selectable: true,
    ...nodeGrouping,
    style: {
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      cursor: 'move'
    }
  };

  if (node?.style) baseNode.style = { ...baseNode.style, ...node.style };

  // Handle different node types (backend may send Agent/Supervisor — normalize case)
  if (normalizedCustomType === NODE_TYPE.AGENT || normalizedCustomType === NODE_TYPE.SUPERVISOR) {
    const existingOtherParams = node.data.agentConfig?.otherParameters || {};
    const migratedParams = {};
    if (node.data.agentConfig?.temperature != null) {
      migratedParams.temperature = node.data.agentConfig.temperature;
    }
    if (node.data.agentConfig?.reasoningEffort) {
      migratedParams.reasoning = { effort: node.data.agentConfig.reasoningEffort };
    }
    if (node.data.agentConfig?.verbosity) {
      migratedParams.verbosity = node.data.agentConfig.verbosity;
    }
    const mergedOtherParameters = { ...migratedParams, ...existingOtherParams };

    const promptRef = node.data.agentConfig?.promptRef || '';
    const promptVersionRef = node.data.agentConfig?.promptVersionRef || '';
    const hasPromptRefs = Boolean(promptRef && promptVersionRef);
    const cataloguePromptMode = isCataloguePromptEditorMode();
    const bindingVersionMismatch = Boolean(node.data.agentConfig?.bindingVersionMismatch);
    // Keep resolvedPrompt for catalogue hydration and for inline-mode empty-prompt fallback (no API).
    const resolvedPromptRaw = node.data.agentConfig?.resolvedPrompt;
    const resolvedPrompt = resolvedPromptRaw && hasPromptRefs ? mapResolvedDocument(resolvedPromptRaw) : null;
    const resolvedMatchesRefs =
      resolvedPromptRaw && hasPromptRefs && resolvedPromptMatchesRefs(promptRef, promptVersionRef, resolvedPromptRaw);
    const useResolvedForInline =
      cataloguePromptMode && hasPromptRefs && resolvedPrompt && (resolvedMatchesRefs || bindingVersionMismatch);
    const effectivePromptVersionRef =
      cataloguePromptMode && bindingVersionMismatch && resolvedPrompt?.promptVersionRef
        ? resolvedPrompt.promptVersionRef
        : promptVersionRef;
    const decodeInlinePrompt = (value) => decodePromptContent(value);
    const getInlinePromptText = (inlineValue, extractFromResolved) => {
      const decodedInline = decodeInlinePrompt(inlineValue);
      const hasExplicitEmptyInline = inlineValue != null && String(inlineValue).trim() === '';

      if (hasExplicitEmptyInline && cataloguePromptMode) {
        return '';
      }

      if (useResolvedForInline) {
        const fromResolved = extractFromResolved(resolvedPrompt);
        if (fromResolved?.trim()) {
          return fromResolved;
        }
      }

      if (decodedInline?.trim()) {
        return decodedInline;
      }

      // Inline mode: if stored inline text is missing, use resolvedPrompt when present (no catalogue API).
      if (!cataloguePromptMode && resolvedPrompt) {
        const fromResolved = extractFromResolved(resolvedPrompt);
        if (fromResolved?.trim()) {
          return fromResolved;
        }
      }

      return decodedInline;
    };
    const inlineSystem = getInlinePromptText(node.data.agentConfig?.prompt, getSystemPromptFromDocument);
    const inlineUser = getInlinePromptText(node.data.agentConfig?.userPrompt, getUserPromptFromDocument);

    baseNode.data = {
      prompt: node.data.label,
      description: node.data.description,
      intentType: node.data.intentType,
      agentId: node.id,
      ...(normalizedCustomType === NODE_TYPE.SUPERVISOR && { supervisorId: node.id }),
      ...(node.data.thinkingMessages && { thinkingMessages: node.data.thinkingMessages }),
      ...(normalizedCustomType === NODE_TYPE.AGENT && {
        agentType: fromBackendAgentType(node.agentType || '')
      }),
      ...(normalizedCustomType === NODE_TYPE.AGENT &&
        node.agentTypeConfig !== undefined && { agentTypeConfig: node.agentTypeConfig }),
      config: buildLoadedAgentConfig(
        node.data.agentConfig,
        mergedOtherParameters,
        node.data.parsers,
        isAgentToAgentType(node.agentType),
        {
          promptRef,
          promptVersionRef: effectivePromptVersionRef,
          resolvedPrompt,
          systemPrompt: inlineSystem,
          userPrompt: inlineUser
        },
        normalizedCustomType === NODE_TYPE.SUPERVISOR
      )
    };
  } else if (node.customType === NODE_TYPE.TOOL) {
    baseNode.data = {
      tool_name: node.data.label,
      toolId: resolveToolDataId(node.data.toolId, node.data),
      description: node.data.description,
      ...(node.data.thinkingMessages && { thinkingMessages: node.data.thinkingMessages }),
      config: {
        name: node.data.toolConfig?.jsMethod || node.data.label,
        description: 'Processes a input and returns the result.',
        parameters: parseZodSchemaFromToolConfig(node.data.toolConfig?.zodSchema),
        implementation: {
          type: 'function',
          code:
            node.data.toolConfig?.jsMethodCode ||
            `async ({ a, b }) => a + b,
  {
    name:        'add',
    description: 'Adds two numbers together',
    schema:      z.object({
      a: z.number().describe('first number'),
      b: z.number().describe('second number'),
    }),
  }`
        },
        responseFormat: node.data.toolConfig?.responseFormat,
        accessControl: {
          requiresAuth: false,
          rateLimit: 100
        }
      },
      toolConfig: node.data.toolConfig
    };
  } else if (node.customType === NODE_TYPE.SKILL) {
    baseNode.data = {
      tool_name: node.data.label,
      toolId: resolveNodeDataId(node.id, node.data.toolId),
      description: node.data.description,
      config: {
        name: node.data.toolConfig?.jsMethod || node.data.label,
        description: node.data.description || 'This skill provides specialized behavior.',
        parameters: parseZodSchemaFromToolConfig(node.data.toolConfig?.zodSchema),
        implementation: {
          type: 'function',
          code: node.data.toolConfig?.jsMethodCode || 'async () => {\n  return "Skill prompt";\n}'
        },
        responseFormat: node.data.toolConfig?.responseFormat,
        accessControl: {
          requiresAuth: false,
          rateLimit: 100
        }
      },
      toolConfig: node.data.toolConfig
    };
  } else if (node.customType === NODE_TYPE.GRAPH) {
    baseNode.data = {
      tool_name: node.data.label,
      toolId: resolveNodeDataId(node.id, node.data.toolId),
      description: node.data.description,
      config: {
        name: node.data.toolConfig?.jsMethod || node.data.label,
        description: node.data.description || 'This graph node provides specialized behavior.',
        parameters: parseGraphZodSchemaPlain(node.data.toolConfig?.zodSchema),
        implementation: {
          type: 'function',
          code: node.data.toolConfig?.jsMethodCode || DEFAULT_GRAPH_JS_METHOD_CODE
        },
        responseFormat: node.data.toolConfig?.responseFormat,
        accessControl: {
          requiresAuth: false,
          rateLimit: 100
        }
      },
      toolConfig: node.data.toolConfig
    };
  } else {
    baseNode.data = {
      label: node.data.label,
      description: node.data.description
    };
  }

  return baseNode;
};

/**
 * Transform a loaded edge from backend format to React Flow format
 * @param {Object} edge - Edge data from backend
 * @param {Array} nodesWithPositions - Transformed nodes array
 * @returns {Object} Transformed edge for React Flow
 */
// Graphs saved before this change must have their sourceHandle migrated on load.
// TO DO: Remove the below code after all the LEGACY GRAPHS ARE MIGRATED
const LEGACY_SOURCE_HANDLE_NODES = new Set([NODE_TYPE.AGENT, NODE_TYPE.TOOL, NODE_TYPE.SKILL, NODE_TYPE.GRAPH]);
const LEGACY_SOURCE_HANDLES = new Set(['top', 'right', 'bottom', 'left']);

const migrateLegacySourceHandle = (sourceHandle, sourceType) => {
  if (LEGACY_SOURCE_HANDLE_NODES.has(sourceType) && LEGACY_SOURCE_HANDLES.has(sourceHandle)) {
    return `${sourceHandle}-source`;
  }
  return sourceHandle;
};

export const transformLoadedEdge = (edge, nodesWithPositions) => {
  const sourceNode = nodesWithPositions.find((n) => n.id === edge.source);
  const targetNode = nodesWithPositions.find((n) => n.id === edge.target);
  const sourceType = sourceNode?.type || NODE_TYPE.AGENT;
  const targetType = targetNode?.type || NODE_TYPE.AGENT;
  const edgeColor = getEdgeColor(sourceType, targetType);

  return {
    id: edge.id,
    source: edge.source,
    target: edge.target,
    type: EDGE_TYPE.CUSTOM,
    animated: true,
    data: {
      label: edge.label || DEFAULT_EDGE_LABEL,
      sourceType,
      targetType,
      conditional: !!edge.condition,
      conditionExpression: edge.condition?.expression || 'true',
      bidirectional: false
    },
    style: {
      stroke: edgeColor,
      strokeDasharray: edge.condition ? '5,5' : 'none'
    },
    markerEnd: {
      type: MarkerType.ArrowClosed,
      color: edgeColor
    },
    sourceHandle: migrateLegacySourceHandle(edge?.sourceHandle || '', sourceType),
    targetHandle: edge?.targetHandle || ''
  };
};

/**
 * Create data for a new START node
 * @returns {Object} { id, data }
 */
export const createStartNodeData = () => ({
  id: NODE_ID_START,
  data: { label: '__start__' }
});

/**
 * Create data for a new END node
 * @returns {Object} { id, data }
 */
export const createEndNodeData = () => ({
  id: NODE_ID_END,
  data: { label: '__end__' }
});

/**
 * Finds the isDefault entry from propertiesData for a given provider.
 * Falls back to the first active entry for that provider if none is marked default.
 * @param {Array} propertiesData
 * @param {string} provider
 * @returns {Object|null}
 */
const getDefaultPropertyEntry = (propertiesData, provider) => {
  if (!Array.isArray(propertiesData) || propertiesData.length === 0) return null;
  return (
    propertiesData.find((entry) => entry.provider === provider && entry.isDefault && entry.isActive) ||
    propertiesData.find((entry) => entry.provider === provider && entry.isActive) ||
    null
  );
};

/**
 * Create data for a new AGENT node
 * @param {Array} nodes - Current canvas nodes (for display-name numbering)
 * @param {Array} [propertiesData=[]] - Model properties from the properties API; used to pre-select the default model
 * @returns {Object} { id, data }
 */
export const createAgentNodeData = (nodes, propertiesData = [], thinkingMessages = null) => {
  const defaultEntry = getDefaultPropertyEntry(propertiesData, 'open-ai');
  const defaultModel = defaultEntry?.model || 'gpt-5-mini';
  const defaultOtherParameters = defaultEntry?.parameters || {};
  const defaultProviderParameters = defaultEntry?.providerParameters || {};
  const agentCount = countDynamicNonToolNodes(nodes) + 1;
  const nodeId = generateDynamicNodeId();

  return {
    id: nodeId,
    data: {
      prompt: `agent${agentCount}`,
      agentId: nodeId,
      config: {
        model: {
          provider: 'open-ai',
          model: defaultModel,
          maxTokens: 1000,
          promptType: ''
        },
        otherParameters: defaultOtherParameters,
        providerParameters: defaultProviderParameters,
        systemPrompt: '',
        userPrompt: '',
        isInternal: false,
        template: '{{input}}',
        memory: {
          type: 'buffer',
          maxMessages: 10,
          reflectionThreshold: 5
        },
        toolCalling: {
          enabled: true,
          toolChoice: 'auto'
        },
        functions: [],
        state: {
          variables: []
        },
        parsers: []
      },
      ...(thinkingMessages && { thinkingMessages })
    }
  };
};

/**
 * Create data for a new TOOL node
 * @param {Array} nodes - Current canvas nodes (for display-name numbering)
 * @returns {Object} { id, data }
 */
export const createToolNodeData = (nodes, thinkingMessages = null) => {
  const toolCount = nodes.filter((n) => n.type === NODE_TYPE.TOOL).length + 1;
  const nodeId = generateToolNodeId();

  return {
    id: nodeId,
    data: {
      tool_name: `tools${toolCount}`,
      toolId: nodeId,
      config: {
        name: `tool_${toolCount}`,
        description: 'Processes a input and returns the result.',
        parameters: {
          type: 'object',
          properties: {
            query: {
              type: 'string',
              description: 'The input parameter for this tool'
            }
          },
          required: ['query']
        },
        implementation: {
          type: 'function',
          endpoint: '',
          method: 'POST',
          headers: {},
          code: `async (query) => {\n  // Implementation goes here\n  const result = { query };\n  return result;\n}`,
          outputFormat: {
            type: 'object',
            properties: {
              result: {
                type: 'string',
                description: 'The result of the tool execution'
              }
            }
          }
        },
        accessControl: {
          requiresAuth: false,
          rateLimit: 100
        }
      },
      ...(thinkingMessages && { thinkingMessages })
    }
  };
};

// Static defaults for content tool nodes — defined once at module level
const getContentToolParameters = (queryParamDescription) => ({
  type: 'object',
  properties: {
    query: {
      type: 'string',
      description: queryParamDescription
    }
  },
  required: ['query']
});

/**
 * @param {string} indexDocumentId
 * @returns {string|null}
 */
export const buildContentToolPayloadFilter = (indexDocumentId) => {
  if (indexDocumentId == null || indexDocumentId === '') return null;
  const escaped = String(indexDocumentId).replace(/'/g, "''");
  return `id eq '${escaped}'`;
};

const getContentToolSearchPayload = (indexDocumentId) => {
  const payload = {
    count: true,
    select: CONTENT_TOOL_CONFIG.API_SELECT_FIELDS,
    vectorFilterMode: 'preFilter',
    vectorQueries: [
      {
        kind: 'text',
        text: CONTENT_TOOL_CONFIG.QUERY_TEMPLATE,
        fields: CONTENT_TOOL_CONFIG.VECTOR_FIELD,
        k: CONTENT_TOOL_CONFIG.VECTOR_K
      }
    ],
    search: CONTENT_TOOL_CONFIG.QUERY_TEMPLATE,
    top: CONTENT_TOOL_CONFIG.TOP_RESULTS
  };
  const filter = buildContentToolPayloadFilter(indexDocumentId);
  if (filter) {
    payload.filter = filter;
  }
  return payload;
};

/**
 * Create data for a new TOOL node from uploaded content.
 * Produces a RestAPI-typed tool node pre-populated to call the content search proxy API.
 * @param {string} fileId - The fileId from the uploaded content (used as toolId)
 * @param {string} fileName - The file name; extension is stripped to form the tool label
 * @param {string} tenantId - The currently selected tenant ID
 * @param {string} [indexDocumentId] - From GET upload/content; becomes apiConfig.payload.filter
 * @param {{ toolDescription?: string, queryParamDescription?: string }} [labels] - Localized tool copy
 * @param {{ start: string, end?: string }} [thinkingMessages] - Default thinking start/end messages
 * @returns {Object} { id, data }
 */
export const createContentToolNodeData = (
  fileId,
  fileName,
  tenantId,
  indexDocumentId,
  labels = {},
  thinkingMessages = null
) => {
  const { toolDescription = '', queryParamDescription = '' } = labels;
  const contentToolParameters = getContentToolParameters(queryParamDescription);
  const contentToolZodSchema = JSON.stringify(contentToolParameters);
  const toolLabel = fileName.replace(/\.[^/.]+$/, '');
  const endpoint = `${env.AI_ADMIN_API_BASE_URL_V2}${CONTENT_TOOL_CONFIG.API_SEARCH_PATH}`;
  const nodeId = generateToolNodeId();

  return {
    id: nodeId,
    data: {
      label: toolLabel,
      tool_name: toolLabel,
      description: toolDescription,
      toolId: fileId,
      config: {
        name: toolLabel,
        description: toolDescription,
        parameters: contentToolParameters
      },
      toolConfig: {
        jsMethod: toolLabel,
        jsMethodCode: CONTENT_TOOL_CONFIG.JS_METHOD_CODE,
        zodSchema: contentToolZodSchema,
        apiConfig: {
          endpoint,
          method: HTTP_METHOD.POST,
          authType: AUTH_TYPE.IES,
          authMethod: AUTH_METHOD.BEARER,
          headers: {
            [BERLIN_TENANT_ID_HEADER]: tenantId,
            'Content-Type': 'application/json'
          },
          payload: getContentToolSearchPayload(indexDocumentId)
        },
        advancedOptions: {
          timeout: CONTENT_TOOL_CONFIG.TIMEOUT,
          retry: CONTENT_TOOL_CONFIG.RETRY
        }
      },
      ...(thinkingMessages && { thinkingMessages })
    }
  };
};

/**
 * Create data for a new SKILL node
 * @param {Array} nodes - Current canvas nodes (for display-name numbering)
 * @returns {Object} { id, data }
 */
export const createSkillNodeData = (nodes) => {
  const skillCount = countDynamicNonToolNodes(nodes) + 1;
  const nodeId = generateDynamicNodeId();

  return {
    id: nodeId,
    data: {
      tool_name: `skill${skillCount}`,
      toolId: nodeId,
      config: {
        name: `skill_${skillCount}`,
        description: 'This skill provides specialized behavior.',
        parameters: {
          type: 'object',
          properties: {},
          required: []
        },
        implementation: {
          type: 'function',
          endpoint: '',
          method: 'POST',
          headers: {},
          code: `async () => {\n  return "Skill prompt goes here";\n}`
        },
        accessControl: {
          requiresAuth: false,
          rateLimit: 100
        }
      }
    }
  };
};

/**
 * Create data for a new graph node (same payload shape as skill; distinct type/color on canvas).
 */
export const createGraphNodeData = (nodes) => {
  const graphCount = countDynamicNonToolNodes(nodes) + 1;
  const nodeId = generateDynamicNodeId();

  return {
    id: nodeId,
    data: {
      tool_name: `graph${graphCount}`,
      toolId: nodeId,
      description: 'This graph node provides specialized behavior.',
      config: {
        name: `graph_${graphCount}`,
        description: 'This graph node provides specialized behavior.',
        parameters: {
          type: 'object',
          properties: {},
          required: []
        },
        implementation: {
          type: 'function',
          endpoint: '',
          method: HTTP_METHOD.POST,
          authType: AUTH_TYPE.NONE,
          code: DEFAULT_GRAPH_JS_METHOD_CODE
        },
        accessControl: {
          requiresAuth: false,
          rateLimit: 100
        }
      },
      toolConfig: {
        apiConfig: {
          graphId: '',
          graphVersion: null,
          endpoint: '',
          method: HTTP_METHOD.POST,
          authType: AUTH_TYPE.NONE,
          authMethod: AUTH_METHOD.BASIC,
          headers: {},
          useStream: false
        },
        zodSchema: JSON.stringify({ type: 'object', properties: {}, required: [] }),
        jsMethod: `graph${graphCount}`,
        jsMethodCode: DEFAULT_GRAPH_JS_METHOD_CODE
      }
    }
  };
};

/**
 * Create data for a new SUPERVISOR node
 * @param {Array} nodes - Current canvas nodes (for display-name numbering)
 * @param {Array} [propertiesData=[]] - Model properties from the properties API; used to pre-select the default model
 * @returns {Object} { id, data }
 */
export const createSupervisorNodeData = (nodes, propertiesData = [], thinkingMessages = null) => {
  const defaultEntry = getDefaultPropertyEntry(propertiesData, 'open-ai');
  const defaultModel = defaultEntry?.model || 'gpt-5-mini';
  const defaultOtherParameters = defaultEntry?.parameters || {};
  const defaultProviderParameters = defaultEntry?.providerParameters || {};
  const supervisorCount = countDynamicNonToolNodes(nodes) + 1;
  const nodeId = generateDynamicNodeId();

  return {
    id: nodeId,
    data: {
      prompt: `supervisor${supervisorCount}`,
      supervisorId: nodeId,
      config: {
        model: {
          provider: 'open-ai',
          model: defaultModel,
          maxTokens: 1000,
          promptType: ''
        },
        otherParameters: defaultOtherParameters,
        providerParameters: defaultProviderParameters,
        systemPrompt:
          `You are supervisor ${supervisorCount}, You are a routing and output agent. ` +
          `Given the user's last message, carefully analyse and execute only those agents and give the output as is from the agents ` +
          `in a clear and separated manner and do not mix the outputs from different agents. ` +
          `Also do not generate anything from your end STRICTLY. In the end just print the name of each agent that were used in individual brackets.`,
        userPrompt: '',
        isInternal: false,
        template: '{{input}}',
        memory: {
          type: 'buffer',
          maxMessages: 10,
          reflectionThreshold: 5
        },
        toolCalling: {
          enabled: false,
          toolChoice: 'auto'
        },
        functions: [],
        state: {
          variables: []
        }
      },
      agentConfigs: [],
      ...(thinkingMessages && { thinkingMessages })
    }
  };
};

/**
 * Create data for a default node
 * @returns {Object} { id, data }
 */
export const createDefaultNodeData = () => ({
  id: generateDynamicNodeId(),
  data: { label: 'Node' }
});

/**
 * Create a new node with common properties
 * @param {string} id - Node ID
 * @param {string} nodeType - Node type
 * @param {Object} data - Node data
 * @returns {Object} Complete node object for React Flow
 */
/**
 * Finds the first grid-aligned position that does not overlap any existing node.
 * @param {Array} existingNodes - Current nodes in the graph
 * @returns {{ x: number, y: number }}
 */
export const getNewNodePosition = (existingNodes) => {
  const { NODE_WIDTH, NODE_HEIGHT, NODE_GAP } = FLOW_CONFIG;
  const cellW = NODE_WIDTH + NODE_GAP;
  const cellH = NODE_HEIGHT + NODE_GAP;

  const overlaps = (x, y) =>
    existingNodes.some((n) => {
      const nx = n.position?.x ?? 0;
      const ny = n.position?.y ?? 0;
      const nw = n.width ?? NODE_WIDTH;
      const nh = n.height ?? NODE_HEIGHT;
      return (
        x < nx + nw + NODE_GAP &&
        x + NODE_WIDTH + NODE_GAP > nx &&
        y < ny + nh + NODE_GAP &&
        y + NODE_HEIGHT + NODE_GAP > ny
      );
    });

  for (let row = 0; row < 20; row += 1) {
    for (let col = 0; col < 20; col += 1) {
      const x = col * cellW;
      const y = row * cellH;
      if (!overlaps(x, y)) return { x, y };
    }
  }

  return { x: existingNodes.length * cellW, y: 0 };
};

export const createNewNode = (id, nodeType, data, position = { x: 0, y: 0 }) => ({
  id,
  type: nodeType,
  data,
  position,
  draggable: true,
  selectable: true,
  style: {
    boxShadow: '0 4px 6px rgba(128, 105, 105, 0.1)',
    cursor: 'move'
  }
});

/**
 * Resolves inner graph from GET /graphs/:id — { graph: { graph: { nodes, rubricData } } } or flat { graph: { nodes } }.
 * @param {Object} data - API response data
 * @returns {Object|null}
 */
export const resolveGraphEntityFromGraphResponse = (data) => {
  if (!data || typeof data !== 'object') {
    return null;
  }
  const outer = data.graph;
  if (!outer || typeof outer !== 'object') {
    return null;
  }
  const inner = outer.graph;
  if (
    inner &&
    typeof inner === 'object' &&
    (Array.isArray(inner.nodes) || inner.rubricData != null || inner.graphId != null)
  ) {
    return inner;
  }
  return outer;
};

export const extractEvaluationAgentTypesFromNodes = (nodes) => {
  if (!Array.isArray(nodes)) {
    return [];
  }
  const seen = new Set();
  const types = [];
  nodes.forEach((node) => {
    if (node?.type !== NODE_TYPE.AGENT && node?.customType !== NODE_TYPE.AGENT) {
      return;
    }
    const label = node?.data?.label ?? node?.data?.prompt;
    if (label == null || label === '') {
      return;
    }
    if (!seen.has(label)) {
      seen.add(label);
      types.push(label);
    }
  });
  return types;
};

export const resolveSelectedEvalTypes = (savedEvalTypes, agentTypes) => {
  const saved = Array.isArray(savedEvalTypes) ? savedEvalTypes.filter((type) => agentTypes.includes(type)) : [];
  if (saved.length) {
    return saved;
  }
  return [...agentTypes];
};

/** Lightweight eval summaries from GET graph — [{ rubricId, rubricName }]. */
export function getEvaluationsSummariesFromGraphPayload(graphPayload) {
  const graph = graphPayload?.graph ?? graphPayload ?? {};
  if (Array.isArray(graph.evaluations) && graph.evaluations.length) {
    return graph.evaluations
      .map((item) => {
        const rubricId = item?.rubricId;
        if (rubricId == null || rubricId === '') {
          return null;
        }
        const rubricName = String(item?.rubricName ?? '').trim() || LEGACY_RUBRIC_LIST_DISPLAY_NAME;
        return { rubricId: String(rubricId), rubricName };
      })
      .filter(Boolean);
  }

  const legacyRubricId = graph.rubricId;
  if (legacyRubricId != null && legacyRubricId !== '') {
    const rubricData = graph.rubricData ?? {};
    const rubricName = String(rubricData.rubricName ?? '').trim() || LEGACY_RUBRIC_LIST_DISPLAY_NAME;
    return [{ rubricId: String(legacyRubricId), rubricName }];
  }

  return [];
}

/** Rubric / evaluation criteria from GET graph payload (rubricData + graph.rubricId). */
export function getEvaluationCriteriaFromGraphPayload(graphPayload) {
  const rubricData = graphPayload?.graph?.rubricData ?? {};
  const evalRubric = rubricData.rubricText ?? rubricData.evalRubric ?? '';
  const sampleRaw = rubricData.evalSamplePercent ?? '';
  const rubricId = graphPayload?.graph?.rubricId ?? null;
  const rubricName = rubricData.rubricName ?? '';

  return {
    evalRubric: typeof evalRubric === 'string' ? evalRubric : '',
    rubricName: typeof rubricName === 'string' ? rubricName : '',
    samplePercent: sampleRaw != null && sampleRaw !== '' ? sampleRaw : '',
    rubricId: rubricId != null && rubricId !== '' ? String(rubricId) : null,
    evalTypes: Array.isArray(rubricData.evalTypes) ? rubricData.evalTypes : [],
    ...scheduleFromRubricData(rubricData)
  };
}

/**
 * Process graph metadata from API response
 * @param {Object} data - API response data
 * @returns {Object} Processed metadata
 */
export const extractGraphMetadata = (data) => {
  const graph = resolveGraphEntityFromGraphResponse(data);
  if (!graph) return null;

  const outer = data.graph || {};
  return {
    versionList: graph.versionList,
    version: graph.version,
    revision: graph.revision ?? 1,
    lifeCycleStatus: graph.lifeCycleStatus,
    name: graph.name,
    graphId: graph.graphId,
    isDefault: graph.isDefault,
    defaultVersion: graph.defaultVersion,
    tenantId: graph.tenantId ?? outer.tenantId,
    isShared: graph.isShared || false,
    accessType: graph.accessType || SHARED_ACCESS_TYPE.VIEW,
    historyExists: data.historyExists || false
  };
};

/**
 * True when the canvas is still the default untitled graph (only START → END scaffolding, no edges).
 */
export const isInitialNewGraph = (nodes, edges) => {
  if (!Array.isArray(edges) || edges.length !== 0) return false;
  if (!Array.isArray(nodes) || nodes.length !== 2) return false;
  const counts = nodes.reduce((acc, n) => {
    const t = n?.type;
    if (t) acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {});
  return counts[NODE_TYPE.START] === 1 && counts[NODE_TYPE.END] === 1;
};

/**
 * Initial state values for graph editor.
 */
export const INITIAL_NODES = [
  createNewNode(NODE_ID_START, NODE_TYPE.START, createStartNodeData().data, { x: 250, y: 50 }),
  createNewNode(NODE_ID_END, NODE_TYPE.END, createEndNodeData().data, { x: 250, y: 280 })
];
export const INITIAL_EDGES = [];
